import { DIFFICULTY_LEVELS, DIFFICULTY_POINTS } from "../../Difficulty/Difficulty";

// The server's limits: PUT matrix/ answers 400 invalid_matrix beyond them.
export const MAX_ROWS = 300;
export const MAX_SUBTOPICS = 100;
export const MAX_COUNT = 999;
export const MAX_WEEK = 20;
export const MAX_HOURS = 99;
export const QUARTERS = [1, 2, 3, 4];
export const ROMAN = { 1: "I", 2: "II", 3: "III", 4: "IV" };
// The grades a matrix plans, in the server's order: 1–12, the teachers' (-1), none (0).
export const GRADES = [...Array.from({ length: 12 }, (_, i) => i + 1), -1, 0];
// A row without a topic (a week of the BTS template waiting for one) needs a school grade of its own.
export const isSchoolGrade = (grade) => Number.isInteger(grade) && grade >= 1 && grade <= 12;
// The grade an address asks for (?grade=, e.g. a subject variant's link to the matrix): one of GRADES, else null.
export const gradeParam = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const grade = Number(value);
  return GRADES.includes(grade) ? grade : null;
};

export const toNumber = (value) => Number(value) || 0;
export const trimName = (name) => (name || "").trim();
// A row's subtopics: [] with a topic plans the whole topic. A pre-v2 server's row has one `subtopic` instead.
export const subtopicsOf = (row) => (Array.isArray(row?.subtopics) ? row.subtopics : row?.subtopic ? [row.subtopic] : []);
// A row's grade: its own (a row without a topic has one), else its topic's; -1 holds the teachers' topics, 0 (or
// none) has no grade.
export const gradeOf = (row) => (Number.isInteger(row?.grade) ? row.grade : Number.isInteger(row?.topic?.grade) ? row.topic.grade : 0);
// A row's identity, for what one matrix changed into another (matrixChanges): its grade, topic and subtopics; a row
// without a topic is told by its week.
export const rowKey = (row) =>
  row.topic
    ? `${gradeOf(row)}|${row.topic.uuid}|${subtopicsOf(row)
        .map((s) => s.uuid)
        .sort()
        .join(",")}`
    : `${gradeOf(row)}|${row.quarter || ""}|${row.week || ""}|none`;

// Whether the server speaks matrix v2: its matrix has `template` (null outside the «БТС-1»…«БТС-4» folders). An
// older server's matrix is only read here: v2 rows saved to it would not mean what they say.
export const isV2 = (matrix) => !!matrix && Object.prototype.hasOwnProperty.call(matrix, "template");
// The server's matrix with its rows in one shape: a pre-v2 row's `subtopic` becomes `subtopics`, its grade its topic's.
export const readMatrix = (data) => data && { ...data, rows: (data.rows || []).map((row) => ({ ...row, grade: gradeOf(row), subtopics: subtopicsOf(row) })) };

export const countOf = (counts) => DIFFICULTY_LEVELS.reduce((sum, level) => sum + toNumber(counts?.[level]), 0);
export const pointsOf = (counts, points = DIFFICULTY_POINTS) =>
  DIFFICULTY_LEVELS.reduce((sum, level) => sum + toNumber(counts?.[level]) * (points[level] || 0), 0);
// "A 2 · B 1 · C 1".
export const levelsText = (counts) => DIFFICULTY_LEVELS.map((level) => `${level} ${toNumber(counts?.[level])}`).join(" · ");
// A row in words, for labels and messages: "Алгебра › Теңсіздіктер; Тізбектер +2", the topic alone when it is planned
// whole, «Тақырып таңдалмаған · 8-апта» without one. `mark` tells apart a topic whose name another of the grade has
// (topicMarks).
export function rowName(t, row, mark = "") {
  if (!row.topic) return [t("folders.matrix.no_topic"), row.week ? t("folders.matrix.week_n", { n: row.week }) : ""].filter(Boolean).join(" · ");
  const topic = trimName(row.topic.name) + (mark ? ` (${mark})` : "");
  const names = subtopicsOf(row).map((s) => trimName(s.name));
  if (!names.length) return topic;
  return `${topic} › ${names.slice(0, 2).join("; ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
}
// A row's topic alone (or that it has none), for a message that says where the row is besides.
export const topicName = (t, row, mark = "") => (row.topic ? trimName(row.topic.name) + (mark ? ` (${mark})` : "") : t("folders.matrix.no_topic"));
// Whether some of the rows have their hours (else their hours are not known, rather than 0).
export const hasHours = (rows) => rows.some((row) => row.hours !== null && row.hours !== undefined && row.hours !== "");

const gradeRank = (grade) => (grade > 0 ? grade : grade === -1 ? 13 : 14);
export const compareGrades = (a, b) => gradeRank(a) - gradeRank(b);
export const gradeLabel = (t, grade) =>
  grade > 0
    ? t("questions_page.filters.grade_n", { grade })
    : grade === -1
      ? t("questions_page.filters.teachers")
      : t("folders.matrix.no_grade");
export const quarterLabel = (t, quarter) =>
  quarter ? t("folders.matrix.quarter_n", { roman: ROMAN[quarter] }) : t("folders.matrix.no_quarter");
// Where a row is in its grade: "III тоқсан · 8-апта" (its grade when it has no quarter).
export const rowPlace = (t, row, separator = " · ") =>
  [row.quarter ? quarterLabel(t, row.quarter) : null, row.week ? t("folders.matrix.week_n", { n: row.week }) : null]
    .filter(Boolean)
    .join(separator) || gradeLabel(t, gradeOf(row));

// The rows in the server's order: grade, quarter, week (a missing one last), then as they were saved or added.
export const compareRows = (a, b) =>
  compareGrades(gradeOf(a), gradeOf(b)) ||
  (a.quarter || QUARTERS.length + 1) - (b.quarter || QUARTERS.length + 1) ||
  (a.week || MAX_WEEK + 1) - (b.week || MAX_WEEK + 1) ||
  toNumber(a.order) - toNumber(b.order);
export const sortRows = (rows) => [...rows].sort(compareRows);

// The quarters of a grade that have rows: one is the current one; of two, the earlier is the previous one; more
// than two get no roles (as the server says them in `grades`).
export function quarterRoles(quarters) {
  const list = [...new Set(quarters.filter(Boolean))].sort((a, b) => a - b);
  if (list.length === 1) return { [list[0]]: "current" };
  if (list.length === 2) return { [list[0]]: "previous", [list[1]]: "current" };
  return {};
}

export const plannedOf = (rows) =>
  Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, rows.reduce((sum, row) => sum + toNumber(row.planned?.[level]), 0)]));

// How a plan compares with the BTS standard ({A: 10, B: 6, C: 4}): per level planned minus standard.
export function standardGaps(planned, standard) {
  if (!standard) return null;
  const gaps = Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, toNumber(planned[level]) - toNumber(standard[level])]));
  return { gaps, ok: DIFFICULTY_LEVELS.every((level) => gaps[level] === 0) };
}

// The BTS template (`template` of a «БТС-1»…«БТС-4» matrix: {round, weeks: [{quarter, week, planned}]}): its week
// in a quarter, and the weeks a grade's rows lack (a row of the same quarter and week has it).
export const templateWeek = (template, quarter, week) => (template?.weeks || []).find((w) => w.quarter === quarter && w.week === week) || null;
export const templateGaps = (template, rows) =>
  (template?.weeks || []).filter((w) => !rows.some((row) => row.quarter === w.quarter && row.week === w.week));

// How the bank covers a saved row's plan: per level the places no approved question takes (`short`, `missing` in
// all: what a variant built from the plan lacks), and how many of them a question not approved yet is written for
// (`unapproved`): with `pending` (a v2 server) the questions in review (sent, under review, resubmitted, returned for
// changes); before it, every question but drafts (`actual`), so declined or archived ones too. A question beyond
// the plan covers nothing.
export function coverage(row) {
  const short = {};
  let missing = 0;
  let unapproved = 0;
  const waiting = (level) =>
    row.pending ? toNumber(row.approved?.[level]) + toNumber(row.pending[level]) : toNumber(row.actual?.[level]);
  DIFFICULTY_LEVELS.forEach((level) => {
    const plan = toNumber(row.planned?.[level]);
    const approved = Math.min(plan, toNumber(row.approved?.[level]));
    const written = Math.max(approved, Math.min(plan, waiting(level)));
    if (plan > approved) short[level] = plan - approved;
    missing += plan - approved;
    unapproved += written - approved;
  });
  return { planned: countOf(row.planned), missing, unapproved, short, none: toNumber(row.actual?.none) };
}

// What tells apart topics of one grade that have the same name (the catalog repeats some): topic uuid -> its code
// («код 23») and, when the code repeats too, its place among them («№2»). `topics`: [{uuid, grade, name, code}].
export function topicMarks(t, topics) {
  const groups = new Map();
  (topics || []).forEach((topic) => {
    const key = `${Number.isInteger(topic.grade) ? topic.grade : 0}|${trimName(topic.name)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(topic);
  });
  const marks = new Map();
  groups.forEach((list) => {
    if (list.length < 2) return;
    const byCode = new Map();
    list.forEach((topic) => {
      const code = String(topic.code ?? "").trim();
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code).push(topic);
    });
    byCode.forEach((same, code) => {
      [...same]
        .sort((a, b) => String(a.uuid).localeCompare(String(b.uuid)))
        .forEach((topic, index) => {
          const parts = [code ? t("folders.matrix.topic_code", { code }) : "", same.length > 1 ? t("folders.matrix.topic_nth", { n: index + 1 }) : ""];
          marks.set(topic.uuid, parts.filter(Boolean).join(" · "));
        });
    });
  });
  return marks;
}

const validCount = (raw) => raw === "" || (/^\d+$/.test(String(raw)) && Number(raw) <= MAX_COUNT);
const validHours = (raw) => raw === "" || (/^\d+$/.test(String(raw)) && Number(raw) <= MAX_HOURS);

// What a 400 invalid_matrix `reason` is about in its row, for the focus: its counts ("A"), hours, week (and quarter)
// or, for the rest, its topic and subtopics.
const REASON_FIELDS = {
  planned_invalid: "A",
  count_out_of_range: "A",
  hours_invalid: "hours",
  quarter_invalid: "week",
  week_invalid: "week",
  week_without_quarter: "week",
  row_without_week: "week",
};
export const errorField = (reason) => REASON_FIELDS[reason] || "topic";

// The server's checks in its order, so a bad row is marked here instead of coming back as a 400: row id -> {message,
// field (what it is about, as errorField says)}. Of two rows in conflict the later one in the server's order is
// marked, as the server names it.
export function validateDraft(rows, t) {
  const errors = new Map();
  const mark = (row, field, key, options) => {
    if (!errors.has(row.id)) errors.set(row.id, { message: t(key, options), field });
  };
  const ordered = sortRows(rows);
  ordered.forEach((row) => {
    if (subtopicsOf(row).length > MAX_SUBTOPICS) mark(row, "topic", "folders.matrix.error_subtopics_max", { max: MAX_SUBTOPICS });
    else if (DIFFICULTY_LEVELS.some((level) => !validCount(row.planned[level]))) mark(row, "A", "folders.matrix.error_range");
    else if (!validHours(row.hours)) mark(row, "hours", "errors.invalid_matrix_hours_invalid");
    else if (row.week && !row.quarter) mark(row, "week", "errors.invalid_matrix_week_without_quarter");
    else if (!row.topic && !isSchoolGrade(gradeOf(row))) mark(row, "topic", "errors.invalid_matrix_grade_invalid");
    else if (!row.topic && !(row.quarter && row.week)) mark(row, "week", "errors.invalid_matrix_row_without_week");
  });
  // A topic is planned whole in one row, or by its subtopics; a subtopic is in one row (named with where it is).
  const whole = new Set();
  const split = new Set();
  const taken = new Map();
  ordered.forEach((row) => {
    if (!row.topic) return;
    const own = subtopicsOf(row);
    const topic = row.topic.uuid;
    const clash = own.find((s) => taken.has(s.uuid));
    if (!own.length && whole.has(topic)) mark(row, "topic", "errors.invalid_matrix_duplicate_row");
    else if (own.length ? whole.has(topic) : split.has(topic)) mark(row, "topic", "errors.invalid_matrix_topic_whole_and_split");
    else if (clash) {
      mark(row, "topic", "folders.matrix.error_subtopic_taken", { subtopic: trimName(clash.name), where: rowPlace(t, taken.get(clash.uuid), ", ") });
    }
    (own.length ? split : whole).add(topic);
    own.forEach((s) => {
      if (!taken.has(s.uuid)) taken.set(s.uuid, row);
    });
  });
  return errors;
}

// A draft row: the counts and hours as typed (strings), the schedule as numbers or null; `approved` the bank's
// counts the steppers show (null when not known).
export const draftRow = (row, id, order) => ({
  id,
  order,
  grade: gradeOf(row),
  topic: row.topic ? { uuid: row.topic.uuid, name: trimName(row.topic.name), grade: row.topic.grade, code: row.topic.code } : null,
  subtopics: subtopicsOf(row).map((s) => ({ uuid: s.uuid, name: trimName(s.name) })),
  quarter: row.quarter || null,
  week: row.quarter ? row.week || null : null,
  hours: row.hours === null || row.hours === undefined ? "" : String(row.hours),
  planned: Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, String(toNumber(row.planned?.[level]))])),
  approved: row.approved || null,
});

const hoursValue = (hours) => (hours === "" || hours === null || hours === undefined ? null : toNumber(hours));

// What a draft saves: the rows in the server's order (a 400 names a row by its index in it). Every row sends its
// `subtopics` ([] plans the topic whole); only a row without a topic sends its `grade` (a topic gives its own).
export const toPayload = (rows) =>
  rows.map((row) => ({
    ...(row.topic ? {} : { grade: gradeOf(row) }),
    topic: row.topic?.uuid || null,
    subtopics: subtopicsOf(row).map((s) => s.uuid),
    quarter: row.quarter || null,
    week: row.quarter ? row.week || null : null,
    hours: hoursValue(row.hours),
    planned: Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, toNumber(row.planned[level])])),
  }));

// What a draft would save, its subtopics in one order (the order they were ticked in changes nothing).
export const signature = (rows) => JSON.stringify(toPayload(sortRows(rows)).map((row) => ({ ...row, subtopics: [...row.subtopics].sort() })));

// What a saved matrix changed into another: [{kind: "added" | "removed" | "changed", row}], a changed row with the row
// it was (`before`). Rows of one identity (rowKey) are the same row; of the rest, a row of the same grade and topic
// (other subtopics), then of the same grade and week (another topic, e.g. a template week given one) is one changed.
export function matrixChanges(before, after) {
  const describe = (row) =>
    JSON.stringify([row.quarter || null, row.week || null, hoursValue(row.hours), ...DIFFICULTY_LEVELS.map((l) => toNumber(row.planned?.[l]))]);
  const left = [...before];
  const added = [];
  const changes = [];
  after.forEach((row) => {
    const index = left.findIndex((old) => rowKey(old) === rowKey(row));
    if (index < 0) {
      added.push(row);
      return;
    }
    const [old] = left.splice(index, 1);
    if (describe(old) !== describe(row)) changes.push({ kind: "changed", row, before: old });
  });
  const sameTopic = (a, b) => gradeOf(a) === gradeOf(b) && !!a.topic && a.topic.uuid === b.topic?.uuid;
  const sameWeek = (a, b) => gradeOf(a) === gradeOf(b) && (a.quarter || null) === (b.quarter || null) && (a.week || null) === (b.week || null);
  [sameTopic, sameWeek].forEach((same) => {
    for (let i = 0; i < added.length; ) {
      const index = left.findIndex((old) => same(old, added[i]));
      if (index < 0) i += 1;
      else {
        const [old] = left.splice(index, 1);
        changes.push({ kind: "changed", row: added[i], before: old });
        added.splice(i, 1);
      }
    }
  });
  added.forEach((row) => changes.push({ kind: "added", row }));
  left.forEach((row) => changes.push({ kind: "removed", row }));
  return changes;
}

// How a row changed, field by field: "тақырыпшалар: +B; −C, сағат 2 → 5, C 1 → 2" (a topic planned whole instead
// of by subtopics, or the other way, as "A; B → Тақырып толығымен").
export function changeDetails(t, before, after) {
  const shown = (value) => (value === null || value === undefined ? "—" : value);
  const names = (list) => list.map((s) => trimName(s.name)).join("; ");
  const parts = [];
  if ((before.topic?.uuid || null) !== (after.topic?.uuid || null)) {
    parts.push(t("folders.matrix.change_topic", { from: topicName(t, before), to: topicName(t, after) }));
  } else if (before.topic && rowKey(before) !== rowKey(after)) {
    const was = subtopicsOf(before);
    const now = subtopicsOf(after);
    if (!was.length || !now.length) {
      const whole = t("folders.matrix.whole_topic");
      parts.push(t("folders.matrix.change_subtopics", { from: names(was) || whole, to: names(now) || whole }));
    } else {
      const added = now.filter((s) => !was.some((w) => w.uuid === s.uuid)).map((s) => `+${trimName(s.name)}`);
      const removed = was.filter((s) => !now.some((n) => n.uuid === s.uuid)).map((s) => `−${trimName(s.name)}`);
      parts.push(t("folders.matrix.change_subtopics_diff", { changes: [...added, ...removed].join("; ") }));
    }
  }
  if ((before.quarter || null) !== (after.quarter || null)) {
    parts.push(t("folders.matrix.change_quarter", { from: ROMAN[before.quarter] || "—", to: ROMAN[after.quarter] || "—" }));
  }
  if ((before.week || null) !== (after.week || null)) {
    parts.push(t("folders.matrix.change_week", { from: shown(before.week || null), to: shown(after.week || null) }));
  }
  if (hoursValue(before.hours) !== hoursValue(after.hours)) {
    parts.push(t("folders.matrix.change_hours", { from: shown(hoursValue(before.hours)), to: shown(hoursValue(after.hours)) }));
  }
  DIFFICULTY_LEVELS.forEach((level) => {
    const from = toNumber(before.planned?.[level]);
    const to = toNumber(after.planned?.[level]);
    if (from !== to) parts.push(`${level} ${from} → ${to}`);
  });
  return parts.join(", ");
}
